import React, { useState, useEffect } from 'react';
import { processRelationshipData } from '../utils/relationshipProcessing';
import relationshipsData from '../data/data_relationships.json';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Table,
  Tabs,
  Select,
  Link,
  StatusIndicator,
  Cards,
  Modal,
  TagEditor,
  ButtonDropdown
} from '@cloudscape-design/components';


export function DataRelationshipsTab({ project }) {
  const [data, setData] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [activeTab, setActiveTab] = useState('relationships');

  useEffect(() => {
    const processed = processRelationshipData(relationshipsData);
    setData(processed);
  }, []);

  if (!data) return null;

  return (
    <SpaceBetween size="l">
      {/* Header Actions */}
      <Container
        header={
          <Header
            variant="h2"
            description="Analysis of data relationships and transaction patterns"
            actions={
              <ButtonDropdown
                items={[
                  { text: 'Export diagram', id: 'export' },
                  { text: 'View as graph', id: 'graph' },
                  { text: 'Refresh analysis', id: 'refresh' }
                ]}
              >
                Actions
              </ButtonDropdown>
            }
          >
            Data Relationships Analysis
          </Header>
        }
      >
        <Tabs
          tabs={[
            {
              label: "Entity Relationships",
              id: "relationships",
              content: (
                <SpaceBetween size="l">
                  <Table
                    columnDefinitions={[
                      {
                        id: 'primary_entity',
                        header: 'Primary Entity',
                        cell: item => (
                          <Link onFollow={() => setSelectedEntity(item)}>
                            {item.primary_entity}
                          </Link>
                        )
                      },
                      {
                        id: 'related_entities',
                        header: 'Related Entities',
                        cell: item => item.relationships.length
                      },
                      {
                        id: 'primary_owner',
                        header: 'Primary Owner',
                        cell: item => {
                          const ownership = data.ownership.find(
                            o => o.entity === item.primary_entity
                          );
                          return ownership?.primary_owner || '-';
                        }
                      },
                      {
                        id: 'sources',
                        header: 'Source Files',
                        cell: item => Array.from(item.sources).join(', ')
                      }
                    ]}
                    items={data.entities}
                    variant="container"
                    stickyHeader
                    stripedRows
                  />

                  {/* Entity Details Modal */}
                  <Modal
                    visible={!!selectedEntity}
                    onDismiss={() => setSelectedEntity(null)}
                    header={`Entity: ${selectedEntity?.primary_entity}`}
                    size="large"
                  >
                    <SpaceBetween size="l">
                      <Container
                        header={<Header variant="h3">Relationships</Header>}
                      >
                        <Table
                          columnDefinitions={[
                            {
                              id: 'related_entity',
                              header: 'Related Entity',
                              cell: item => item.related_entity
                            },
                            {
                              id: 'type',
                              header: 'Type',
                              cell: item => item.type
                            },
                            {
                              id: 'cardinality',
                              header: 'Cardinality',
                              cell: item => item.cardinality
                            },
                            {
                              id: 'lifecycle',
                              header: 'Lifecycle',
                              cell: item => item.lifecycle_dependency
                            }
                          ]}
                          items={selectedEntity?.relationships || []}
                          variant="embedded"
                        />
                      </Container>

                      <Container
                        header={<Header variant="h3">Access Patterns</Header>}
                      >
                        <Table
                          columnDefinitions={[
                            {
                              id: 'component',
                              header: 'Component',
                              cell: item => item.component
                            },
                            {
                              id: 'access_type',
                              header: 'Access Type',
                              cell: item => item.access_type
                            },
                            {
                              id: 'frequency',
                              header: 'Frequency',
                              cell: item => (
                                <StatusIndicator type={
                                  item.frequency === 'High' ? 'success' :
                                  item.frequency === 'Medium' ? 'warning' : 'info'
                                }>
                                  {item.frequency}
                                </StatusIndicator>
                              )
                            }
                          ]}
                          items={data.ownership.find(
                            o => o.entity === selectedEntity?.primary_entity
                          )?.access_patterns || []}
                          variant="embedded"
                        />
                      </Container>
                    </SpaceBetween>
                  </Modal>
                </SpaceBetween>
              )
            },
            {
              label: "Transaction Boundaries",
              id: "transactions",
              content: (
                <SpaceBetween size="l">
                  <Cards
                    cardDefinition={{
                      header: item => (
                        <Header
                          variant="h3"
                          actions={
                            <Link onFollow={() => setSelectedTransaction(item)}>
                              View details
                            </Link>
                          }
                        >
                          {item.process}
                        </Header>
                      ),
                      sections: [
                        {
                          id: 'source',
                          header: 'Source',
                          content: item => <Box>{item.source_file}</Box>
                        },
                        {
                          id: 'operations',
                          header: 'Operations',
                          content: item => (
                            <Box>
                              {item.atomic_operations.join(', ')}
                            </Box>
                          )
                        },
                        {
                          id: 'entities',
                          header: 'Involved Entities',
                          content: item => (
                            <Box>
                              {item.involved_entities.join(', ')}
                            </Box>
                          )
                        }
                      ]
                    }}
                    items={data.transactions}
                    cardsPerRow={[
                      { cards: 1 },
                      { minWidth: 500, cards: 2 }
                    ]}
                  />

                  {/* Transaction Details Modal remains the same */}
                </SpaceBetween>
              )
            },
            {
              label: "Data Ownership",
              id: "ownership",
              content: (
                <Table
                  columnDefinitions={[
                    {
                      id: 'entity',
                      header: 'Entity',
                      cell: item => item.entity
                    },
                    {
                      id: 'owner',
                      header: 'Primary Owner',
                      cell: item => item.primary_owner
                    },
                    {
                      id: 'sources',
                      header: 'Source Files',
                      cell: item => Array.from(item.sources).join(', ')
                    },
                    {
                      id: 'read_frequency',
                      header: 'Read Access',
                      cell: item => {
                        const readPattern = item.access_patterns.find(
                          p => p.access_type === 'Read'
                        );
                        return readPattern ? (
                          <StatusIndicator type={
                            readPattern.frequency === 'High' ? 'success' :
                            readPattern.frequency === 'Medium' ? 'warning' : 'info'
                          }>
                            {readPattern.frequency}
                          </StatusIndicator>
                        ) : '-';
                      }
                    },
                    {
                      id: 'write_frequency',
                      header: 'Write Access',
                      cell: item => {
                        const writePattern = item.access_patterns.find(
                          p => p.access_type === 'Write'
                        );
                        return writePattern ? (
                          <StatusIndicator type={
                            writePattern.frequency === 'High' ? 'success' :
                            writePattern.frequency === 'Medium' ? 'warning' : 'info'
                          }>
                            {writePattern.frequency}
                          </StatusIndicator>
                        ) : '-';
                      }
                    }
                  ]}
                  items={data.ownership}
                  variant="container"
                  stickyHeader
                  stripedRows
                />
              )
            }
          ]}
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        />
      </Container>
    </SpaceBetween>
  );
};
